import React from "react";
import type { Theme } from "@material-ui/core";
import {
  Box,
  Collapse,
  createStyles,
  debounce,
  FormControl,
  IconButton,
  InputAdornment,
  makeStyles,
  Menu,
  MenuItem,
  Select,
  TextField,
} from "@material-ui/core";

import {
  ChatOutlined as ChatOutlinedIcon,
  ExpandMore as ExpandMoreIcon,
  InsertChartOutlinedSharp as InsertChartOutlinedSharpIcon,
  MenuOutlined as MenuOutlinedIcon,
  MergeTypeOutlined as MergeTypeOutlinedIcon,
  PlayCircleFilledWhiteOutlined as PlayCircleFilledWhiteOutlinedIcon,
  Search as SearchIcon,
  SettingsInputAntennaOutlined as SettingsInputAntennaOutlinedIcon,
} from "@eroad/icons/material";

import TrackingSettings from "./TrackingSettings";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    formControl: {
      width: "33%",
      "& svg": {
        top: "calc(50% - 14px)",
      },
      "& > div > div": {
        paddingRight: "0!important" as const,
      },
    },
    selectEmpty: {
      marginTop: theme.spacing(2),
    },
    search: {
      width: "100%",
      "& > div": {
        width: "100%",
        paddingRight: "5px",
      },
      "& > input": {
        fontSize: "14px",
        fontWeight: "500",
        color: "rgba(0, 0, 0, 0.38)",
      },
      "& button": {
        padding: 0,
      },
    },
    adornedEnd: {
      paddingRight: 0,
    },
    expandButton: {
      padding: "4px 0px 4px 8px",
      "&:hover": {
        backgroundColor: "transparent",
      },
    },
  }),
);

interface SearchToolProps {
  query?: string;
  fleets?: Record<string, string>;
  vehicleView?: Record<string, string>;
  views?: Record<string, string>;
  selectedFleet?: string;
  selectedVehicleView?: string;
  selectedView?: string;
  onChangeQuery: (query: string) => void;
  onChangeFleet?: (fleet: string) => void;
  onChangeVehicleView?: (vehicleView: string) => void;
  onChangeView?: (view: string) => void;
}

const SearchTool = ({
  query,
  fleets,
  vehicleView,
  views,
  selectedFleet,
  selectedVehicleView,
  selectedView,
  onChangeQuery,
  onChangeFleet,
  onChangeVehicleView,
  onChangeView,
}: SearchToolProps): JSX.Element => {
  const classes = useStyles();
  const [expanded, setExpanded] = React.useState<boolean>(true);
  const [searchQuery, setSearchQuery] = React.useState<string>(query ?? "");
  const [fleet, setFleet] = React.useState<string>(selectedFleet ?? (fleets && Object.keys(fleets)[0]) ?? "0");
  const [vehicles, setVehicles] = React.useState<string>(
    selectedVehicleView ?? (vehicleView && Object.keys(vehicleView)[0]) ?? "0",
  );
  const [view, setView] = React.useState<string>(selectedView ?? (views && Object.keys(views)[0]) ?? "0");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onQueryChange = React.useCallback(debounce(onChangeQuery, 500), []);
  const onQuery = (evnt: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(evnt.target.value);
    onQueryChange(evnt.target.value);
  };
  const onSelectFleet = (evnt: React.ChangeEvent<{ name?: string | undefined; value: unknown }>) => {
    setFleet(evnt.target.value as string);
    if (onChangeFleet) onChangeFleet(evnt.target.value as string);
  };
  const onSelectVehicleView = (evnt: React.ChangeEvent<{ name?: string | undefined; value: unknown }>) => {
    setVehicles(evnt.target.value as string);
    if (onChangeVehicleView) onChangeVehicleView(evnt.target.value as string);
  };
  const onSelectView = (evnt: React.ChangeEvent<{ name?: string | undefined; value: unknown }>) => {
    setView(evnt.target.value as string);
    if (onChangeView) onChangeView(evnt.target.value as string);
  };

  // menu
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const onMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget);
  const onMenuClose = () => setAnchorEl(null);
  const [showSettings, setShowSettings] = React.useState<boolean>(false);

  return (
    <Box width="auto" p={4}>
      <Box display="flex" width="auto">
        <Box flexGrow={1} width={1}>
          <TextField
            id="inspector-search"
            variant="outlined"
            placeholder="Search for a vehicle"
            size="small"
            value={searchQuery}
            onChange={onQuery}
            className={classes.search}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton>
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
        {!expanded && (
          <Box>
            <IconButton onClick={() => setExpanded(true)} className={classes.expandButton}>
              <ExpandMoreIcon />
            </IconButton>
          </Box>
        )}
      </Box>
      <Collapse in={expanded}>
        <Box width={1}>
          <TrackingSettings
            title="Tracking settings"
            subtitle="Live tracking"
            show={showSettings}
            onClose={() => setShowSettings(false)}
          />
          <Box mt={3}>
            <FormControl className={classes.formControl}>
              <Select labelId="fleet-label" id="fleet-select" value={fleet} onChange={onSelectFleet} disableUnderline>
                {fleets &&
                  Object.keys(fleets).map((f: string) => (
                    <MenuItem key={`${f}`} value={f}>
                      {fleets[f]}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <FormControl className={classes.formControl}>
              <Select
                labelId="vehicle-label"
                id="vehicle-select"
                value={vehicles}
                onChange={onSelectVehicleView}
                disableUnderline
              >
                {vehicleView &&
                  Object.keys(vehicleView).map((veh: string) => (
                    <MenuItem key={`${veh}`} value={veh}>
                      {vehicleView[veh]}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <FormControl className={classes.formControl}>
              <Select labelId="view-label" id="view-select" value={view} onChange={onSelectView} disableUnderline>
                {views &&
                  Object.keys(views).map((v: string) => (
                    <MenuItem key={`${v}`} value={v}>
                      {views[v]}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Box>
          <Box display="flex" justifyContent="space-evenly" mt={3}>
            <IconButton size="small">
              <ChatOutlinedIcon />
            </IconButton>
            <IconButton size="small">
              <SettingsInputAntennaOutlinedIcon />
            </IconButton>
            <IconButton size="small">
              <MergeTypeOutlinedIcon />
            </IconButton>
            <IconButton size="small">
              <PlayCircleFilledWhiteOutlinedIcon />
            </IconButton>
            <IconButton size="small">
              <InsertChartOutlinedSharpIcon />
            </IconButton>
            <IconButton size="small" onClick={onMenuOpen}>
              <MenuOutlinedIcon />
            </IconButton>
            <Menu id="tools-menu" anchorEl={anchorEl} keepMounted open={Boolean(anchorEl)} onClose={onMenuClose}>
              <MenuItem
                onClick={() => {
                  onMenuClose();
                  setExpanded(false);
                }}
              >
                HideTools
              </MenuItem>
              <MenuItem onClick={onMenuClose}>Zoom to all</MenuItem>
              <MenuItem
                onClick={() => {
                  onMenuClose();
                  setShowSettings(true);
                }}
              >
                Tracking settings
              </MenuItem>
            </Menu>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};
export default SearchTool;
